import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { mockCases } from '@/mocks/cases';

export default function ArchivePage() {
  const [search, setSearch] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState<string | null>(null);
  const [restored, setRestored] = useState<string[]>([]);

  const archivedCases = mockCases.filter((c) => c.status === 'closed' || c.status === 'archived');
  const filtered = archivedCases.filter((c) =>
    !restored.includes(c.id) && (
      c.childName.toLowerCase().includes(search.toLowerCase()) ||
      c.caseNumber.toLowerCase().includes(search.toLowerCase())
    )
  );

  const handleRestore = (id: string) => {
    setRestored((prev) => [...prev, id]);
    setShowRestoreModal(null);
  };

  return (
    <AppLayout title="Case Archive" breadcrumb={['Admin', 'Archive']}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex-1 min-w-48">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search archived cases..." className="bg-transparent text-sm outline-none w-full placeholder-gray-400 text-gray-700" />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 px-3 py-2 rounded-xl">
          <i className="ri-archive-line" />
          {filtered.length} archived cases
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-700 text-sm">Archived & Closed Cases</h3>
          <p className="text-xs text-gray-400 mt-0.5">All case data is retained and encrypted per RA 10173</p>
        </div>
        <div className="divide-y divide-gray-50">
          {filtered.map((c) => (
            <div key={c.id} className="px-5 py-4 flex flex-wrap items-center gap-4">
              <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-folder-lock-line text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-semibold text-gray-800">{c.childName}</p>
                  <span className="text-xs font-mono text-gray-400">{c.caseNumber}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.riskLevel === 'critical' ? 'bg-red-100 text-red-600' : c.riskLevel === 'high' ? 'bg-orange-100 text-orange-600' : c.riskLevel === 'moderate' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>{c.riskLevel}</span>
                </div>
                <p className="text-xs text-gray-500">{c.incidentType} · {c.municipality}, {c.province}</p>
                <p className="text-xs text-gray-400">Worker: {c.assignedWorker} · Closed: {c.updatedAt}</p>
              </div>
              <div className="flex gap-2">
                <button className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-eye-line" />View
                </button>
                <button className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-download-line" />Export
                </button>
                <button onClick={() => setShowRestoreModal(c.id)} className="text-xs bg-teal-100 text-teal-700 px-3 py-1.5 rounded-lg hover:bg-teal-200 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-refresh-line" />Restore
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="p-16 text-center text-gray-400">
              <i className="ri-archive-line text-4xl mb-2 block" />
              <p className="text-sm">No archived cases found</p>
            </div>
          )}
        </div>
      </div>

      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-refresh-line text-teal-500 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Restore Case?</h3>
            <p className="text-sm text-gray-500 mb-5">This case will be moved back to active cases and reassigned to the original social worker.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setShowRestoreModal(null)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleRestore(showRestoreModal)} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Restore Case</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
